<template>
  <div class="bz-slider">
    <!-- The track and its active part are the slider's own, under PrimeVue's (made transparent): an
         active part that starts at the centre is a shape PrimeVue's range does not draw. -->
    <span class="bz-slider__track" aria-hidden="true">
      <span class="bz-slider__active" :style="activeStyle"></span>
    </span>
    <Slider
      class="bz-slider__control"
      :min="min"
      :max="max"
      :step="step"
      :model-value="modelValue"
      :aria-labelledby="labelledBy"
      @update:model-value="(value) => $emit('update:modelValue', value)"
    />
  </div>
</template>

<script>
import Slider from 'primevue/slider';

/**
 * A slider, the one both systems share: a thin rounded track in the control grey, the active part in
 * the action blue and a round white thumb. `centered` starts the active part at the middle of the range
 * (exposure: from 0 to its value, either way); otherwise at the minimum.
 *
 * @rule   `labelledBy` is the id of the visible label: the slider's accessible name.
 * @rule   The thumb is drawn 28 px and grabbed through a 48 px circle around it (norm 9).
 * @why    PrimeVue starts a drag only from the handle — a tap on the track jumps the value there but
 *         does not keep following the finger — so the handle is what must be easy to catch with a thumb.
 */
export default {
  name: 'BzSlider',
  components: { Slider },
  props: {
    modelValue: { type: Number, default: 0 },
    min: { type: Number, required: true },
    max: { type: Number, required: true },
    step: { type: Number, default: 1 },
    labelledBy: { type: String, required: true },
    centered: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  computed: {
    activeStyle() {
      const span = this.max - this.min || 1;
      const at = (Math.min(this.max, Math.max(this.min, this.modelValue)) - this.min) / span;
      const from = this.centered ? 0.5 : 0;
      const start = Math.min(from, at);
      const size = Math.abs(at - from);
      return { left: `${start * 100}%`, width: `${size * 100}%` };
    },
  },
};
</script>

<style scoped>
.bz-slider {
  position: relative;
  display: flex;
  align-items: center;
  height: var(--bz-target-min);
}

.bz-slider__track {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: var(--bz-slider-track);
  margin-top: calc(var(--bz-slider-track) / -2);
  border-radius: var(--bz-radius-full);
  background: var(--bz-control);
  overflow: hidden;
}

.bz-slider__active {
  position: absolute;
  top: 0;
  bottom: 0;
  background: var(--bz-track-active);
}

.bz-slider__control {
  position: relative;
  width: 100%;
}

.bz-slider :deep(.p-slider-handle::after) {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: var(--bz-target-min);
  height: var(--bz-target-min);
  margin: calc(var(--bz-target-min) / -2) 0 0 calc(var(--bz-target-min) / -2);
  border-radius: 50%;
}
</style>
